import { useIntl } from 'react-intl';

import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
import OfficeBuildingIcon from '@atlaskit/icon/core/office-building';
import { FlyoutBody, FlyoutFooter, FlyoutHeader } from '@atlaskit/side-nav-items/flyout-menu-item';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';

import { useRoutes } from '@/common/hooks';
import { FASTSHIFT_PATH_KEYS, FASTSHIFT_PATHS } from '@/common/routes/constants';

import { messages } from './messages';
import {
	OrganizationsFlyoutEmptyState,
	OrganizationsFlyoutErrorState,
	OrganizationsFlyoutLoadingState,
} from './organizations-flyout-states';
import { getOrganizationDisplayName } from './utils';

import type { Organization } from './types';

export type OrganizationsFlyoutContentProps = {
	error: string | null;
	organizations: Organization[] | null;
	pending: boolean;
	onClose: () => void;
	onOrganizationSelect: (organizationId: string) => void;
	onViewAllOrganizationsSelect: () => void;
};

const OrganizationsFlyoutBodyContent = ({
	error,
	organizations,
	pending,
	onClose,
	onOrganizationSelect,
}: OrganizationsFlyoutContentProps) => {
	const intl = useIntl();
	const { getPathByOrganizationId } = useRoutes();

	if (organizations?.length) {
		return (
			<MenuList>
				{organizations.map((organization) => (
					<LinkMenuItem
						href={getPathByOrganizationId(
							FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.ORGANIZATION_DETAILS],
							organization.orgId,
						)}
						key={organization.orgId}
						testId={`organizations-flyout-item-${organization.orgId}`}
						elemBefore={<OfficeBuildingIcon label="" color="currentColor" />}
						onClick={() => {
							onOrganizationSelect(organization.orgId);
							onClose();
						}}
					>
						{getOrganizationDisplayName(organization, intl)}
					</LinkMenuItem>
				))}
			</MenuList>
		);
	}

	if (pending) {
		return <OrganizationsFlyoutLoadingState />;
	}

	if (error) {
		return <OrganizationsFlyoutErrorState />;
	}

	if (organizations === null) {
		return <OrganizationsFlyoutLoadingState />;
	}

	return <OrganizationsFlyoutEmptyState />;
};

export const OrganizationsFlyoutContent = ({
	error,
	organizations,
	pending,
	onClose,
	onOrganizationSelect,
	onViewAllOrganizationsSelect,
}: OrganizationsFlyoutContentProps) => {
	const { formatMessage } = useIntl();
	const { paths } = useRoutes();

	return (
		<>
			<FlyoutHeader
				title={formatMessage(messages.flyoutTitle)}
				closeButtonLabel={formatMessage(messages.closeFlyoutLabel)}
			/>
			<FlyoutBody testId="organizations-flyout-body">
				<OrganizationsFlyoutBodyContent
					error={error}
					organizations={organizations}
					pending={pending}
					onClose={onClose}
					onOrganizationSelect={onOrganizationSelect}
					onViewAllOrganizationsSelect={onViewAllOrganizationsSelect}
				/>
			</FlyoutBody>
			<FlyoutFooter testId="organizations-flyout-footer">
				<MenuList>
					<LinkMenuItem
						href={paths.domainScopedOrganizations ?? ''}
						onClick={() => {
							onViewAllOrganizationsSelect();
							onClose();
						}}
						elemBefore={<AlignTextLeftIcon label="" color="currentColor" />}
					>
						{formatMessage(messages.viewAllOrganizationsLabel)}
					</LinkMenuItem>
				</MenuList>
			</FlyoutFooter>
		</>
	);
};
